// Context picker — chip row + module/template popovers, styled to
// sq_context_picker_mock.html exactly (via sqAi.module.css). Pure presentation
// + popover plumbing; all scope state lives in usePicker.

import React, { useEffect, useRef, useState } from "react";
import styles from "../sqAi.module.css";
import { moduleHasTemplates } from "../usePicker";
import { ModuleIcon } from "./icons";

function templateCountLabel(templates) {
  if (!templates.length) return "All templates";
  if (templates.length === 1) return templates[0];
  return `${templates.length} templates`;
}

export default function ContextPicker({
  available,
  availableLoading,
  availableError,
  selected,
  templates,
  toggleModule,
  removeModule,
  onTemplatesChange,
  loadTemplates,
  getTemplateEntry,
  disabled,
}) {
  const [openPanel, setOpenPanel] = useState(null); // null | "modules" | "templates"
  const rootRef = useRef(null);

  const single = selected.length === 1 ? selected[0] : null;
  const labelOf = (canonical) => {
    const m = available.find((x) => x.canonical === canonical);
    return m ? m.label : canonical;
  };

  useEffect(() => {
    if (openPanel === "templates" && !single) setOpenPanel(null);
  }, [openPanel, single]);

  useEffect(() => {
    if (disabled) setOpenPanel(null);
  }, [disabled]);

  useEffect(() => {
    if (!openPanel) return;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpenPanel(null);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpenPanel(null);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [openPanel]);

  const openModules = () => setOpenPanel((p) => (p === "modules" ? null : "modules"));
  const openTemplates = () => {
    if (!single) return;
    loadTemplates(single);
    setOpenPanel((p) => (p === "templates" ? null : "templates"));
  };
  const toggleTemplate = (title) =>
    onTemplatesChange(templates.includes(title) ? templates.filter((t) => t !== title) : [...templates, title]);

  const tplEntry = single ? getTemplateEntry(single) : null;

  return (
    <div className={styles.panelHost} ref={rootRef}>
      <div className={styles.chiprow}>
        <button
          type="button"
          className={styles.askBtn}
          onClick={openModules}
          disabled={disabled}
          aria-haspopup="true"
          aria-expanded={openPanel === "modules"}
        >
          <span className={styles.at}>＋</span> Ask about…
        </button>

        {selected.map((canonical) => {
          const paired = single === canonical && moduleHasTemplates(canonical);
          const chip = (
            <span className={styles.chip}>
              <ModuleIcon canonical={canonical} />
              <span>{labelOf(canonical)}</span>
              <button
                type="button"
                className={styles.chipX}
                onClick={() => removeModule(canonical)}
                aria-label={`Remove ${labelOf(canonical)}`}
              >
                ✕
              </button>
            </span>
          );
          if (!paired) return <React.Fragment key={canonical}>{chip}</React.Fragment>;
          return (
            <span key={canonical} className={styles.pair}>
              {chip}
              <button
                type="button"
                className={`${styles.tplBtn} ${templates.length ? "" : styles.tplBtnEmpty}`}
                onClick={openTemplates}
                aria-haspopup="true"
                aria-expanded={openPanel === "templates"}
                title="Choose templates"
              >
                <span className={styles.tri}>▸</span>
                <span>{templateCountLabel(templates)}</span>
                <span className={styles.tri}>▾</span>
              </button>
            </span>
          );
        })}
      </div>

      {openPanel === "modules" && (
        <div className={`${styles.panelPop} ${styles.panelModules}`} role="dialog" aria-label="Add context">
          <h4>Add context</h4>
          <div className={styles.panelScroll}>
            {availableLoading ? (
              <p className={styles.panelMsg}>Loading modules…</p>
            ) : availableError ? (
              <p className={styles.panelMsgError}>{availableError}</p>
            ) : available.length === 0 ? (
              <p className={styles.panelMsg}>No modules available for this tenant.</p>
            ) : (
              available.map((m) => {
                const sel = selected.includes(m.canonical);
                return (
                  <button
                    type="button"
                    key={m.canonical}
                    className={`${styles.opt} ${sel ? styles.optSel : ""}`}
                    onClick={() => toggleModule(m.canonical)}
                    aria-pressed={sel}
                  >
                    <ModuleIcon canonical={m.canonical} />
                    <span className={styles.optLabel}>{m.label}</span>
                    <span className={styles.cb}>✓</span>
                  </button>
                );
              })
            )}
          </div>
          <div className={styles.panelDone}>
            <button type="button" onClick={() => setOpenPanel(null)}>Done</button>
          </div>
        </div>
      )}

      {openPanel === "templates" && single && (
        <div className={`${styles.panelPop} ${styles.panelTemplates}`} role="dialog" aria-label="Templates">
          <h4>Templates</h4>
          <div className={styles.panelScroll}>
            {!tplEntry || tplEntry.loading ? (
              <p className={styles.panelMsg}>Loading templates…</p>
            ) : tplEntry.error ? (
              <p className={styles.panelMsgError}>{tplEntry.error}</p>
            ) : (tplEntry.items || []).length === 0 ? (
              <p className={styles.panelMsg}>No templates — all records will be used.</p>
            ) : (
              (tplEntry.items || []).map((t) => {
                const sel = templates.includes(t.title);
                return (
                  <button
                    type="button"
                    key={t.id}
                    className={`${styles.opt} ${sel ? styles.optSel : ""}`}
                    onClick={() => toggleTemplate(t.title)}
                    aria-pressed={sel}
                  >
                    <span className={styles.optLabel}>{t.title}</span>
                    <span className={styles.cb}>✓</span>
                  </button>
                );
              })
            )}
          </div>
          <div className={styles.panelDone}>
            <button type="button" onClick={() => setOpenPanel(null)}>Done</button>
          </div>
        </div>
      )}
    </div>
  );
}
